/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddConnection from "./AddConnection";
import TabPanels from "../../../ui/tabs/TabPanels";
import Tabs from "../../../ui/tabs/Tabs";
import TabList from "../../../ui/tabs/TabList";
import Button from "../../buttons/Button";
import { errorAlert, makeApiRequest } from "../../../util";
import AuthContext from "../../../store/authContext";
import { getXSSIConnectionByRef } from "../../../api";
import UpdateUnitRanges from "../ranges/UpdateUnitRanges";
import UpdateConnection from "../editXSSIConnection/UpdateConnection";
import SuccessAnimation from "../../../ui/animation/SuccessAnimation";
import UpdateGroupRanges from "../ranges/UpdateGroupRanges";
import InlineLoader from "../../loader/InlineLoader";
import {
  CREATE_XSSI_CONNECTION_TRANSLATIONS,
  LOADING_TEXT_TRANSLATIONS,
} from "../../../translations/util";

interface IProps {
  org: string;
}

const TABS_LIST = CREATE_XSSI_CONNECTION_TRANSLATIONS.tabList;
const previousStep = TABS_LIST.length - 1;

const NewXSSIConnection = ({ org }: IProps) => {
  const {
    token,
    scopes: { xssi_connections: xssiConnections },
  } = useContext(AuthContext);

  const hasEditPermission = xssiConnections && xssiConnections.includes("u");
  const hasDeletePermission = xssiConnections && xssiConnections.includes("d");

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [newRef, setNewRef] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [connectionData, setConnectionData] = useState({
    "CONNECTION-ID": "",
    CreationDate: "",
    ModificationDate: "",
    "RADIO-IPV4-ADDR": "",
    "RADIO-RFSS-ID": 0,
    "RADIO-SYSTEM-ID": 0,
    "RADIO-WACN-ID": 0,
    "RADIO-RFSS-4-HEX-DIGIT": false,
    "IWF-RFSS-ID": 0,
    "IWF-SYSTEM-ID": 0,
    "IWF-WACN-ID": 0,
    "RESPONSES-BEFORE-CONNECTED": 0,
    "MISSED-RESPONSES-BEFORE-DISCONNECTED": 0,
    "VOCODER-MODES": [],
    "UNIT-RANGES": [
      {
        "UNIT-MIN-ID": 1,
        "UNIT-MAX-ID": 10,
        ID: 1,
        CreationDate: "",
        ModificationDate: "",
      },
    ],
    "IWF-UNIT-RANGES": [
      {
        "IWF-UNIT-MIN-ID": 1,
        "IWF-UNIT-MAX-ID": 10,
        ID: 1,
        CreationDate: "",
        ModificationDate: "",
      },
    ],
    "IWF-GROUP-RANGES": [
      {
        "GROUP-MIN-ID": 1,
        "GROUP-MAX-ID": 10,
        ID: 1,
        CreationDate: "",
        ModificationDate: "",
      },
    ],
    "RADIO-GROUP-RANGES": [
      {
        "GROUP-MIN-ID": 1,
        "GROUP-MAX-ID": 10,
        ID: 1,
        CreationDate: "",
        ModificationDate: "",
      },
    ],
    "POLLING-ENABLED": false,
    "POLLING-INTERVAL-CONNECTED": "",
    "POLLING-INTERVAL-DISCONNECTED": "",
  });

  const [initialConnectionData, setInitialConnectionData] =
    useState(connectionData);

  const getConnectionByRef = async (ref: string) => {
    if (org && token) {
      try {
        const response = await getXSSIConnectionByRef({
          token,
          org,
          ref,
        });
        setConnectionData({
          "CONNECTION-ID": response["CONNECTION-ID"],
          CreationDate: response.CreationDate,
          ModificationDate: response.ModificationDate,
          "RADIO-IPV4-ADDR": response["RADIO-IPV4-ADDR"],
          "RADIO-RFSS-ID": response["RADIO-RFSS-ID"],
          "RADIO-SYSTEM-ID": response["RADIO-SYSTEM-ID"],
          "RADIO-WACN-ID": response["RADIO-WACN-ID"],
          "RADIO-RFSS-4-HEX-DIGIT": response["RADIO-RFSS-4-HEX-DIGIT"],
          "IWF-RFSS-ID": response["IWF-RFSS-ID"],
          "IWF-SYSTEM-ID": response["IWF-SYSTEM-ID"],
          "IWF-WACN-ID": response["IWF-WACN-ID"],
          "RESPONSES-BEFORE-CONNECTED": response["RESPONSES-BEFORE-CONNECTED"],
          "MISSED-RESPONSES-BEFORE-DISCONNECTED":
            response["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
          "VOCODER-MODES": response["VOCODER-MODES"],
          "UNIT-RANGES": response["UNIT-RANGES"],
          "IWF-UNIT-RANGES": response["IWF-UNIT-RANGES"],
          "IWF-GROUP-RANGES": response["IWF-GROUP-RANGES"],
          "RADIO-GROUP-RANGES": response["RADIO-GROUP-RANGES"],
          "POLLING-ENABLED": response["POLLING-ENABLED"],
          "POLLING-INTERVAL-CONNECTED": response["POLLING-INTERVAL-CONNECTED"],
          "POLLING-INTERVAL-DISCONNECTED":
            response["POLLING-INTERVAL-DISCONNECTED"],
        });
        setInitialConnectionData({
          "CONNECTION-ID": response["CONNECTION-ID"],
          CreationDate: response.CreationDate,
          ModificationDate: response.ModificationDate,
          "RADIO-IPV4-ADDR": response["RADIO-IPV4-ADDR"],
          "RADIO-RFSS-ID": response["RADIO-RFSS-ID"],
          "RADIO-SYSTEM-ID": response["RADIO-SYSTEM-ID"],
          "RADIO-WACN-ID": response["RADIO-WACN-ID"],
          "RADIO-RFSS-4-HEX-DIGIT": response["RADIO-RFSS-4-HEX-DIGIT"],
          "IWF-RFSS-ID": response["IWF-RFSS-ID"],
          "IWF-SYSTEM-ID": response["IWF-SYSTEM-ID"],
          "IWF-WACN-ID": response["IWF-WACN-ID"],
          "RESPONSES-BEFORE-CONNECTED": response["RESPONSES-BEFORE-CONNECTED"],
          "MISSED-RESPONSES-BEFORE-DISCONNECTED":
            response["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
          "VOCODER-MODES": response["VOCODER-MODES"],
          "UNIT-RANGES": response["UNIT-RANGES"],
          "IWF-GROUP-RANGES": response["IWF-GROUP-RANGES"],
          "RADIO-GROUP-RANGES": response["RADIO-GROUP-RANGES"],
          "POLLING-ENABLED": response["POLLING-ENABLED"],
          "POLLING-INTERVAL-CONNECTED": response["POLLING-INTERVAL-CONNECTED"],
          "POLLING-INTERVAL-DISCONNECTED":
            response["POLLING-INTERVAL-DISCONNECTED"],
          "IWF-UNIT-RANGES": response["IWF-UNIT-RANGES"],
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const updateXSSIConnections = async () => {
    if (newRef) {
      setLoading(true);
      try {
        await makeApiRequest<{ Ref: string }>({
          path: `/api/v1/app-config/xssi-connections/${newRef}`,
          method: "PUT",
          body: JSON.stringify({
            "RADIO-IPV4-ADDR": connectionData["RADIO-IPV4-ADDR"],
            "RADIO-RFSS-ID": connectionData["RADIO-RFSS-ID"],
            "RADIO-SYSTEM-ID": connectionData["RADIO-SYSTEM-ID"],
            "RADIO-WACN-ID": connectionData["RADIO-WACN-ID"],
            "RADIO-RFSS-4-HEX-DIGIT": connectionData["RADIO-RFSS-4-HEX-DIGIT"],
            "IWF-RFSS-ID": connectionData["IWF-RFSS-ID"],
            "IWF-SYSTEM-ID": connectionData["IWF-SYSTEM-ID"],
            "IWF-WACN-ID": connectionData["IWF-WACN-ID"],
            "RESPONSES-BEFORE-CONNECTED":
              connectionData["RESPONSES-BEFORE-CONNECTED"],
            "MISSED-RESPONSES-BEFORE-DISCONNECTED":
              connectionData["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
            "VOCODER-MODES": connectionData["VOCODER-MODES"],
            "POLLING-ENABLED": connectionData["POLLING-ENABLED"],
            "POLLING-INTERVAL-CONNECTED":
              connectionData["POLLING-INTERVAL-CONNECTED"],
            "POLLING-INTERVAL-DISCONNECTED":
              connectionData["POLLING-INTERVAL-DISCONNECTED"],
          }),
          token,
          emptyResponse: true,
          org,
        });
        getConnectionByRef(newRef);
        // successAlert("Connection updated successfully!");
        setShowSuccessAnimation(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetConnectionChanges = () => {
    setConnectionData(initialConnectionData);
  };

  useEffect(() => {
    if (newRef) {
      setDataLoading(true);
      getConnectionByRef(newRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newRef]);
  return (
    <div className="data-tabs">
      <Tabs
        selectedIndex={step}
        onChange={({ selectedIndex }: { selectedIndex: number }) => {
          if (newRef) {
            setStep(selectedIndex);
          }
        }}
      >
        <div className="data-tablist">
          <TabList
            currentIndex={step}
            TABS_LIST={TABS_LIST}
            ariaLabel="New Connection Form Data"
            showCheckmark={!!newRef}
          />
        </div>

        {newRef ? (
          <TabPanels>
            {showSuccessAnimation ? (
              <SuccessAnimation
                onComplete={() => {
                  setShowSuccessAnimation(false);
                }}
                msg={CREATE_XSSI_CONNECTION_TRANSLATIONS.successAlertMessage}
              />
            ) : dataLoading ? (
              <InlineLoader
                description={LOADING_TEXT_TRANSLATIONS.defaultLoadingText}
              />
            ) : (
              <UpdateConnection
                connectionData={connectionData}
                setConnectionData={setConnectionData}
                initialConnectionData={initialConnectionData}
                xssiRef={newRef}
                updateXSSIConnections={updateXSSIConnections}
                resetConnectionChanges={resetConnectionChanges}
                loading={loading}
                hasEditPermission={hasEditPermission}
                inCreateForm
                setCreateEditing={setIsEditing}
              />
            )}
            <UpdateUnitRanges
              ranges={connectionData["UNIT-RANGES"]}
              xssiRef={newRef}
              org={org}
              refetchConnection={() => getConnectionByRef(newRef)}
              hasEditPermission={hasEditPermission}
              hasDeletePermission={hasDeletePermission}
              unitType="UNIT"
            />
            <UpdateUnitRanges
              ranges={connectionData["IWF-UNIT-RANGES"]}
              xssiRef={newRef}
              org={org}
              refetchConnection={() => getConnectionByRef(newRef)}
              hasEditPermission={hasEditPermission}
              hasDeletePermission={hasDeletePermission}
              unitType="IWF"
            />
            <UpdateGroupRanges
              ranges={connectionData["IWF-GROUP-RANGES"]}
              xssiRef={newRef}
              org={org}
              refetchConnection={() => getConnectionByRef(newRef)}
              hasEditPermission={hasEditPermission}
              hasDeletePermission={hasDeletePermission}
              groupName="IWF"
            />
            <UpdateGroupRanges
              ranges={connectionData["RADIO-GROUP-RANGES"]}
              xssiRef={newRef}
              org={org}
              refetchConnection={() => getConnectionByRef(newRef)}
              hasEditPermission={hasEditPermission}
              hasDeletePermission={hasDeletePermission}
              groupName="RADIO"
            />
          </TabPanels>
        ) : (
          <TabPanels>
            <AddConnection setNewRef={setNewRef} />
          </TabPanels>
        )}
      </Tabs>

      {newRef && !isEditing ? (
        <div className="flex justify-end form-footer">
          <div className="mr-2">
            {step === 0 ? (
              <Button
                className="w-full"
                type="button"
                kind="tertiary"
                onClick={() =>
                  navigate("/configurationManagement/xssiConnections")
                }
                label={CREATE_XSSI_CONNECTION_TRANSLATIONS.labels.backToListing}
                dataCy="xssiBackToListing-"
              />
            ) : null}

            {step !== 0 ? (
              <Button
                className="w-full"
                type="button"
                kind="tertiary"
                label={
                  CREATE_XSSI_CONNECTION_TRANSLATIONS.labels.previousButton
                }
                onClick={() => setStep((step) => step - 1)}
                dataCy="xssiPrevious-"
              />
            ) : null}
          </div>

          <div>
            {step === 0 ? (
              <Button
                className="w-full"
                type="button"
                onClick={() => setStep((step) => step + 1)}
                label={
                  CREATE_XSSI_CONNECTION_TRANSLATIONS.labels
                    .proceedToRangesButon
                }
                dataCy="xssiProceedToRanges-"
              />
            ) : null}

            {step > 0 && step < previousStep ? (
              <Button
                className="w-full"
                type="button"
                onClick={() => setStep((step) => step + 1)}
                label={CREATE_XSSI_CONNECTION_TRANSLATIONS.labels.nextButton}
                dataCy="xssiNext-"
              />
            ) : null}

            {step === previousStep ? (
              <Button
                className="w-full"
                type="button"
                label={CREATE_XSSI_CONNECTION_TRANSLATIONS.labels.finishButton}
                onClick={() =>
                  navigate("/configurationManagement/xssiConnections")
                }
                dataCy="xssiFinish-"
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NewXSSIConnection;
